import { useMemo, useRef, useState } from "react";
import { SnackbarProvider } from "notistack";
import { SnackbarsCustomStyle, AUTO_HIDE_DURATION, MAX_SNACK } from "@Layouts/components/Snackbars";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import { useChangeRequestSubscriptions } from "@/Washouts/hooks/ChangeRequests/useChangeRequestSubscriptions";
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";

// Hooks
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";

// Helpers
import { isEveryProviderCompleted } from "@/Washouts/helpers/Statuses";

// Constants
import actions from "@/Constants/actionsList";

// Icons
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import PayrollClosingContainer from "./PayrollClosingContainer";
import PayPeriodSideBar from "@/Layouts/components/SideBars/PayPeriodSideBar";
import { useOnUpdateCommissionCycle } from "@/Washouts/hooks/common/useOnUpdateCommissionCycle";

function findNextPayPeriod(sortedPayPeriods: IPayPeriod[], currentPayPeriodIndex = 0): IPayPeriod | null {
	if (!sortedPayPeriods.length) {
		return null;
	}
	const currentPayPeriod = sortedPayPeriods[currentPayPeriodIndex];
	let index = currentPayPeriodIndex + 1;
	while (index < sortedPayPeriods.length) {
		if (sortedPayPeriods[index]?._id !== currentPayPeriod?._id) {
			return sortedPayPeriods[index];
		}
		index++;
	}
	return null;
}

export default function PayrollClosingLayer() {
	const { useAuth } = useAuthorization();
	const viewCommissionCycleClosing = useAuth(actions.VIEW_COMMISSION_CYCLE_CLOSING);

	const [current, setCurrent] = useState(false);
	const lastSelected = useRef<IPayrollClosingSideBarSelected | null>(null);
	const [selection, setSelection] = useState<IPayrollClosingSideBarSelected | null>(null);

	const commissionCycleId = selection?.cycleId ?? "";
	const payPeriodId = selection?.payPeriodId ?? "";

	const { isLoading, locationId, commissionCycle, payPeriod, allCommissionCycles, partnerPayPeriod } = useCommissionCyclesData({
		commissionCycleId,
		payPeriodId,
	});

	const allPayPeriods = useMemo(() => allCommissionCycles?.flatMap((cycle) => cycle.payPeriods) ?? [], [allCommissionCycles]);

	const disableNotLast = payPeriod?.positionStatus.every((status) => status.status === "UNREVIEWABLE") ?? false;

	const SideBar = useMemo(() => {
		const sideSelection = function (value: IPeriodTreeSelection, currentCommissionCycle = false) {
			const selected = value as IPayrollClosingSideBarSelected;
			if (selected?._id !== lastSelected.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);
				setCurrent(currentCommissionCycle);
			}
		};

		return (
			<PayPeriodSideBar
				disablePayPeriodNotLast={disableNotLast}
				selectDefaultLastCommissionCycle
				isMultiple
				callbackSelected={sideSelection}
			/>
		);
	}, []);

	useChangeRequestSubscriptions({});

	useApproveRetractWashoutSubscription({
		locationId,
		commissionCycleId: commissionCycle?._id,
		payPeriodId: payPeriod?._id,
		updateCommissionCycle: true,
	});

	useApproveRetractWashoutSubscription({
		locationId,
		commissionCycleId: payPeriod?.nextCCId ?? payPeriod?.previousCCId,
		payPeriodId: payPeriod?._id,
		updateCommissionCycle: true,
	});

	const allPositionsReady = Boolean(
		selection?.positionStatus?.every((status) => ["UNREVIEWABLE", "POSTED_PAYROLL"].includes(status.status))
	);

	const isCompletedPayPeriod = isEveryProviderCompleted(payPeriod);
	const sortedPayPeriods = allPayPeriods?.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
	const currentPayPeriodIndex = sortedPayPeriods?.findIndex((payPeriodItem) => payPeriodItem._id === payPeriodId);
	const nextPayPeriod = findNextPayPeriod(sortedPayPeriods, currentPayPeriodIndex);
	const isNextPayPeriodOpen = Boolean(nextPayPeriod?.payPeriodProviders?.some((provider) => provider.status !== "COMPLETED"));
	const canCreateAdjustments = isCompletedPayPeriod && isNextPayPeriodOpen && allPositionsReady;

	useOnUpdateCommissionCycle({
		locationId,
		commissionCycleId: selection?.cycleId,
		returnFields: `locationId
			status
			payPeriods {
				_id
				title
				intervalCode
				startDate
				endDate
				inReview
				drawIngested
                nextCCId
                previousCCId
                status
                positionStatus {
                    position
                    status
                }
				payPeriodProviders {
					_id
					status
					providerPayPeriodId
				}
			}`,
	});

	return (
		<>
			{viewCommissionCycleClosing && (
				<SnackbarProvider
					maxSnack={MAX_SNACK}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					autoHideDuration={AUTO_HIDE_DURATION}
					iconVariant={{
						error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
						success: <DoneIcon fontSize="small" className="mr-2" />,
					}}
					Components={{
						error: SnackbarsCustomStyle,
						success: SnackbarsCustomStyle,
					}}
				>
					<BodyLayer fixedContent={SideBar}>
						<PayrollClosingContainer
							selectedItem={selection}
							current={current}
							selection={!!selection}
							locationId={locationId}
							commissionCyclesLoading={isLoading}
							commissionCycle={commissionCycle}
							payPeriod={payPeriod}
							canCreateAdjustments={canCreateAdjustments}
							partnerPayPeriod={partnerPayPeriod}
						/>
					</BodyLayer>
				</SnackbarProvider>
			)}
		</>
	);
}
